import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Select } from '../components/Field'
import { StatusDot } from '../components/StatusDot'
import { PubBadge } from '../components/PubBadge'
import { api, type EventSubject, type WebhookEvent } from '../api/client'
import { fmtDateTime, fmtRelative } from '../lib/format'

export function EventsPage() {
  const [status, setStatus] = useState<string>('')
  // Mailgun outnumbers SimpleCirc roughly 199:1 in a default page, so an
  // unfiltered log buries the circulation events the Account column is for.
  // Switch Source to All to see delivery traffic.
  const [source, setSource] = useState<string>('simplecirc')

  const { data, isLoading } = useQuery({
    queryKey: ['events', status, source],
    queryFn: () =>
      api.get<{ items: WebhookEvent[] }>(
        `/api/events?limit=200${status ? `&status=${status}` : ''}${source ? `&source=${source}` : ''}`,
      ),
    refetchInterval: 15_000,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Telemetry</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Event Log
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { value: '', label: 'All' },
              { value: 'success', label: 'Success' },
              { value: 'duplicate', label: 'Duplicate' },
              { value: 'failed', label: 'Failed' },
              { value: 'pending', label: 'Pending' },
            ]}
          />
          <Select
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            options={[
              { value: '', label: 'All' },
              { value: 'simplecirc', label: 'SimpleCirc' },
              { value: 'mailgun', label: 'Mailgun' },
            ]}
          />
        </div>
      </header>

      <Panel label="Recent Events" bodyClassName="p-0" accent="cyan">
        {isLoading && (
          <div className="px-4 py-10 text-center label text-paper-trace">Loading…</div>
        )}
        {!isLoading && (data?.items.length ?? 0) === 0 && (
          <div className="px-4 py-10 text-center label text-paper-trace">No events</div>
        )}
        {!isLoading && (data?.items.length ?? 0) > 0 && (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>Received</Th>
                <Th>Age</Th>
                <Th>Source</Th>
                <Th>Event</Th>
                <Th>Account</Th>
                <Th>Publication</Th>
                <Th>Status</Th>
                <Th>Processed</Th>
                <Th>Error</Th>
              </tr>
            </thead>
            <tbody>
              {data!.items.map((e) => (
                <tr key={e.id} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                  <Td mono subtle>{fmtDateTime(e.received_at)}</Td>
                  <Td mono subtle>{fmtRelative(e.received_at)}</Td>
                  <Td mono>{e.source}</Td>
                  <Td mono>{e.event_type}</Td>
                  <Td className="max-w-[260px]">
                    <SubjectCell subject={e.subject} />
                  </Td>
                  <Td>
                    {e.publication_slug ? <PubBadge slug={e.publication_slug} compact /> : '—'}
                  </Td>
                  <Td>
                    <StatusBadge status={e.processed_status} />
                  </Td>
                  <Td mono subtle>{fmtDateTime(e.processed_at)}</Td>
                  <Td mono subtle className="max-w-[280px] truncate">
                    {e.error || '—'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

function SubjectCell({ subject }: { subject?: EventSubject | null }) {
  if (!subject) return <span className="text-paper-dim">—</span>

  // Mailgun: the recipient address is the whole story — no account behind it
  // to open, since much of the mail on that account was never ours.
  if (subject.kind === 'email') {
    return (
      <span className="font-mono text-[11px] text-paper-dim truncate block">
        {subject.label}
      </span>
    )
  }

  return (
    <div className="leading-tight">
      {subject.subscriber_id ? (
        <Link
          to={`/subscribers/${subject.subscriber_id}`}
          className="text-[13px] text-paper hover:text-amber-glow hover:underline truncate block"
        >
          {subject.label}
        </Link>
      ) : (
        // Not ingested yet — name it, but there's nothing to open.
        <span className="text-[13px] text-paper-dim truncate block">{subject.label}</span>
      )}
      {subject.account_id && (
        <span className="font-mono text-[10px] text-paper-trace tabular-nums">
          {subject.account_id}
        </span>
      )}
    </div>
  )
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="label py-2 px-4 font-normal text-left">{children}</th>
}

function Td({
  children, mono, subtle, className = '',
}: {
  children: React.ReactNode
  mono?: boolean
  subtle?: boolean
  className?: string
}) {
  return (
    <td
      className={`py-2 px-4
        ${mono ? 'font-mono text-[11px] tabular-nums' : 'text-[13px]'}
        ${subtle ? 'text-paper-dim' : 'text-paper'}
        ${className}`}
    >
      {children}
    </td>
  )
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'success' ? 'ok' :
    status === 'duplicate' ? 'muted' :
    status === 'failed' ? 'err' :
    status === 'pending' ? 'active' : 'warn'
  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusDot tone={tone} />
      <span className="label">{status}</span>
    </span>
  )
}
